import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupIcon from '@mui/icons-material/Group';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import HubIcon from '@mui/icons-material/Hub';
import ForumIcon from '@mui/icons-material/Forum';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';

const WorkspaceTable = ({
  rows,
  isLoading,
  onView,
  onEdit,
  onSoftDelete,
  onRestore,
  onDelete,
  onManageUsers,
  onManageModels,
  onShowModels,
  onShowConversations
}) => {
  const [selectedTab, setSelectedTab] = useState('active');

  const activeRows = useMemo(
    () => (rows || []).filter((item) => !item?.soft_delete?.deleted),
    [rows]
  );
  const deletedRows = useMemo(
    () => (rows || []).filter((item) => item?.soft_delete?.deleted),
    [rows]
  );

  const visibleRows = selectedTab === 'active' ? activeRows : deletedRows;

  const handleTabChange = (_event, value) => {
    setSelectedTab(value);
  };

  const renderStatusChip = (workspace) => {
    if (workspace?.soft_delete?.deleted) {
      return (
        <Chip
          size="small"
          label="Eliminado"
          sx={{
            bgcolor: 'var(--wb-danger-16)',
            color: 'var(--wb-danger)',
            fontWeight: 600
          }}
        />
      );
    }
    if (workspace?.is_active) {
      return (
        <Chip
          size="small"
          label="Activo"
          sx={{
            bgcolor: 'var(--wb-x-bg-4ade8029, rgba(74,222,128,0.16))',
            color: 'var(--wb-success)',
            fontWeight: 600
          }}
        />
      );
    }
    return (
      <Chip
        size="small"
        label="Inactivo"
        sx={{
          bgcolor: 'var(--wb-border-16)',
          color: 'var(--wb-muted)',
          fontWeight: 600
        }}
      />
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: 'var(--wb-bg-panel)',
        borderRadius: 3,
        border: '1px solid var(--wb-border)',
        p: { xs: 1.5, md: 2.5 }
      }}
    >
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        sx={{
          '& .MuiTab-root': {
            color: 'var(--wb-muted-70)',
            textTransform: 'none',
            fontWeight: 600,
            minWidth: 0,
            mr: 2
          },
          '& .MuiTab-root.Mui-selected': {
            color: 'var(--wb-text-title)'
          },
          '& .MuiTabs-indicator': {
            backgroundColor: 'var(--wb-x-bg-5b21b6, #5b21b6)',
            height: 3,
            borderRadius: '3px 3px 0 0'
          }
        }}
      >
        <Tab label={`Activos (${activeRows.length})`} value="active" />
        <Tab label={`Eliminados (${deletedRows.length})`} value="deleted" />
      </Tabs>

      <Box sx={{ mt: 2.5 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={36} />
          </Box>
        ) : visibleRows.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              borderRadius: 2,
              background: 'var(--wb-bg-overlay-60)',
              border: '1px dashed var(--wb-border-20)'
            }}
          >
            <InfoOutlinedIcon sx={{ color: 'var(--wb-muted-70)' }} />
            <Typography sx={{ color: 'var(--wb-muted-70)', mt: 1.5 }}>
              {selectedTab === 'active'
                ? 'No se encontraron espacios de análisis activos.'
                : 'No hay espacios eliminados de forma lógica.'}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {visibleRows.map((workspace) => {
              const modelsCount = workspace.models_linked?.length || 0;
              const conversationsCount = workspace.conversations?.length || 0;
              const usersCount = workspace.users_allowed?.length || 0;
              const versionLabel = workspace.version ? `Versión ${workspace.version}` : 'Sin versión';

              return (
                <Box
                  key={workspace.semantic_workspace_id}
                  sx={{
                    borderRadius: 3,
                    background:
                      selectedTab === 'deleted'
                        ? 'linear-gradient(135deg, var(--wb-x-text-f8717114, rgba(248,113,113,0.08)), var(--wb-x-text-ef44440d, rgba(239,68,68,0.05)))'
                        : 'linear-gradient(135deg, var(--wb-x-text-2563eb14, rgba(37,99,235,0.08)), var(--wb-x-text-6d28d90f, rgba(109,40,217,0.06)))',
                    border: '1px solid var(--wb-border-10)',
                    p: { xs: 2, md: 2.5 }
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                      <Avatar
                        sx={{
                          bgcolor: 'var(--wb-x-bg-1d4ed8, #1d4ed8)',
                          width: 48,
                          height: 48,
                          fontWeight: 700
                        }}
                      >
                        <AutoAwesomeMotionIcon />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.75 }}>
                          <Typography
                            variant="h6"
                            sx={{ color: 'var(--wb-text-title)', fontWeight: 700, fontSize: '1.05rem' }}
                          >
                            {workspace.name || 'Sin nombre'}
                          </Typography>
                          {renderStatusChip(workspace)}
                          <Chip
                            size="small"
                            label={versionLabel}
                            sx={{
                              bgcolor: 'var(--wb-x-bg-60a5fa1f, rgba(96,165,250,0.12))',
                              color: 'var(--wb-blue)',
                              fontWeight: 600
                            }}
                          />
                        </Stack>
                        <Typography sx={{ color: 'var(--wb-muted-80)', mb: 1.5 }}>
                          {workspace.description || 'Sin descripción registrada.'}
                        </Typography>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1.5}
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          sx={{ color: 'var(--wb-text-label)' }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 999,
                              backgroundColor: 'var(--wb-x-bg-60a5fa1f, rgba(96,165,250,0.12))'
                            }}
                          >
                            <HubIcon fontSize="small" sx={{ color: 'var(--wb-blue)' }} />
                            <Typography
                              component="button"
                              type="button"
                              onClick={() => onShowModels?.(workspace)}
                              sx={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                color: 'var(--wb-blue)',
                                cursor: 'pointer',
                                fontWeight: 500
                              }}
                            >
                              {modelsCount} modelos incluidos
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 999,
                              backgroundColor: 'var(--wb-x-bg-60a5fa14, rgba(96,165,250,0.08))'
                            }}
                          >
                            <GroupIcon fontSize="small" sx={{ color: 'var(--wb-accent)' }} />
                            <Typography
                              component="button"
                              type="button"
                              onClick={() => onManageUsers?.(workspace)}
                              sx={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                color: 'var(--wb-accent)',
                                cursor: 'pointer',
                                fontWeight: 500
                              }}
                            >
                              {usersCount} usuarios autorizados
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 999,
                              backgroundColor: 'var(--wb-x-bg-60a5fa14, rgba(96,165,250,0.08))'
                            }}
                          >
                            <ForumIcon fontSize="small" sx={{ color: 'var(--wb-accent)' }} />
                            <Typography
                              component="button"
                              type="button"
                              onClick={() => onShowConversations?.(workspace)}
                              sx={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                color: 'var(--wb-accent)',
                                cursor: 'pointer',
                                fontWeight: 500
                              }}
                            >
                              {conversationsCount} conversaciones
                            </Typography>
                          </Stack>
                        </Stack>
                        <Typography
                          sx={{
                            mt: 1.5,
                            fontSize: '0.78rem',
                            color: 'var(--wb-x-text-94a3b8a6, rgba(148,163,184,0.65))'
                          }}
                        >
                          Id espacio: <span style={{ fontFamily: 'monospace' }}>{workspace.semantic_workspace_id}</span>
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title="Ver detalle">
                        <IconButton
                          onClick={() => onView?.(workspace)}
                          sx={{ color: 'var(--wb-text)' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {selectedTab === 'active' ? (
                        <>
                          <Tooltip title="Modificar">
                            <IconButton
                              onClick={() => onEdit?.(workspace)}
                              sx={{ color: 'var(--wb-accent)' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Administrar modelos">
                            <IconButton
                              onClick={() => onManageModels?.(workspace)}
                              sx={{ color: 'var(--wb-blue)' }}
                            >
                              <ManageAccountsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Agregar o eliminar usuarios">
                            <IconButton
                              onClick={() => onManageUsers?.(workspace)}
                              sx={{ color: 'var(--wb-success-strong)' }}
                            >
                              <GroupAddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminación soft">
                            <IconButton
                              onClick={() => onSoftDelete?.(workspace)}
                              sx={{ color: 'var(--wb-warning)' }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title="Restaurar">
                            <IconButton
                              onClick={() => onRestore?.(workspace)}
                              sx={{ color: 'var(--wb-success)' }}
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Gestionar usuarios">
                            <IconButton
                              onClick={() => onManageUsers?.(workspace)}
                              sx={{ color: 'var(--wb-accent)' }}
                            >
                              <GroupAddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminación total">
                            <IconButton
                              onClick={() => onDelete?.(workspace)}
                              sx={{ color: 'var(--wb-danger)' }}
                            >
                              <DeleteSweepIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Paper>
  );
};

WorkspaceTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  onView: PropTypes.func,
  onEdit: PropTypes.func,
  onSoftDelete: PropTypes.func,
  onRestore: PropTypes.func,
  onDelete: PropTypes.func,
  onManageUsers: PropTypes.func,
  onManageModels: PropTypes.func,
  onShowModels: PropTypes.func,
  onShowConversations: PropTypes.func
};

WorkspaceTable.defaultProps = {
  rows: [],
  isLoading: false,
  onView: undefined,
  onEdit: undefined,
  onSoftDelete: undefined,
  onRestore: undefined,
  onDelete: undefined,
  onManageUsers: undefined,
  onManageModels: undefined,
  onShowModels: undefined,
  onShowConversations: undefined
};

export default WorkspaceTable;
